import React, { useState, useEffect, useRef } from 'react';
import { fetchRankings, fetchRankingTotals } from '../api/client';
import RankingTable from '../components/RankingTable';
import Poster from '../components/Poster';
import Podium from '../components/Podium';
import { MoreButton } from '../components/VenueCharts';
import { PAGE_MAX, fmt, card, ltr, Chip, Head, Stepper, PhoneRow, CustomRange, Occ as OccPct, sharePct as pct } from '../components/RankingParts';
import { israelToday, shiftStr, fmtShortRange } from '../components/DateRangePicker';
import { useAuth } from '../context/AuthContext';
import { ageText } from '../components/ageLabel';
import useWindowWidth from '../hooks/useWindowWidth';

/**
 * דירוגים (§12.11) — one page:
 *  - this year, this month and today as three totals with their leader; each
 *    picks the period of the ranking below. All time and a custom range are
 *    quiet links under them.
 *  - the categories as chips: all films, age, distributor, genre (each a
 *    ranking of its groups first, a click opens one group's films), dubbed
 *    into Hebrew, Israeli, IMAX (Planet's IMAX halls, their tickets only).
 *  - the ranking: its own total, a podium (5 places on a wide card, 3 on a
 *    phone), and a table from #1 — rows instead of a table on a phone.
 * The page state lives in App, so a visit to a film and back lands here again.
 */

const HEB_MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const pad = (n) => String(n).padStart(2, '0');
const ymd = (s) => s.split('-').map(Number);

const CATS = [
  { key: 'all', label: 'כל הסרטים' },
  { key: 'age', label: 'לפי גיל' },
  { key: 'distributor', label: 'לפי מפיץ' },
  { key: 'genre', label: 'לפי ז׳אנר' },
  { key: 'dubbed', label: 'מדובב לעברית' },
  { key: 'israeli', label: 'ישראלי' },
  { key: 'imax', label: 'IMAX' },
];
const CAT_TITLE = { all: 'כל הסרטים', age: 'הגבלת גיל', distributor: 'מפיצים', genre: 'ז׳אנרים',
                    dubbed: 'מדובב לעברית', israeli: 'ישראלי', imax: 'IMAX' };
const GROUP_WORD = { age: 'הגבלות', distributor: 'מפיצים', genre: 'ז׳אנרים' };
const GROUP_HEAD = { age: 'הגבלת גיל', distributor: 'מפיץ', genre: 'ז׳אנר' };
const GROUP_ALL = { age: 'כל הגילאים', distributor: 'כל המפיצים', genre: 'כל הז׳אנרים' };
// A group's name as shown: age ratings like "16+" read left to right.
const groupName = (cat, name) => (cat === 'age' ? ageText(name) : name);
const NONE_LABEL = { age: 'ללא הגבלת גיל', distributor: 'ללא מפיץ', genre: 'ללא ז׳אנר' };

export const defaultRankView = () => ({ period: 'year', anchor: israelToday(), custom: null, cat: 'all', sub: null, dub: 'all' });

/* ── periods ───────────────────────────────────────────────────────────── */
function rangeOf(v) {
  const [y, m] = ymd(v.anchor);
  if (v.period === 'year') return { start: `${y}-01-01`, end: `${y}-12-31` };
  if (v.period === 'month') return { start: `${y}-${pad(m)}-01`, end: `${y}-${pad(m)}-${pad(new Date(y, m, 0).getDate())}` };
  if (v.period === 'day') return { start: v.anchor, end: v.anchor };
  if (v.period === 'custom' && v.custom) return v.custom;
  return null;
}
function stepAnchor(v, dir) {
  const [y, m] = ymd(v.anchor);
  if (v.period === 'year') return `${y + dir}-01-01`;
  if (v.period === 'month') { const d = new Date(y, m - 1 + dir, 1); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`; }
  return shiftStr(v.anchor, dir);
}
// The ranking shows exactly the period a total card stands for.
function isCurrent(v, today) {
  const r = rangeOf(v);
  return !!r && ['year', 'month', 'day'].includes(v.period) && r.start <= today && today <= r.end;
}
function periodLabel(v, today) {
  const [y, m, d] = ymd(v.anchor);
  if (v.period === 'year') return String(y);
  if (v.period === 'month') return `${HEB_MONTHS[m - 1]} ${y}`;
  if (v.period === 'day') return v.anchor === today ? <>היום · {ltr(`${d}.${m}`)}</> : ltr(`${d}.${m}.${y}`);
  if (v.period === 'custom' && v.custom) return ltr(fmtShortRange(v.custom.start, v.custom.end));
  return 'כל הזמנים';
}

/* ── small pieces ──────────────────────────────────────────────────────── */
const sharePct = (r) => pct(r.share, r.tickets);
const Share = ({ r }) => <span dir="ltr" style={{ unicodeBidi: 'isolate', color: 'var(--muted)' }}>{sharePct(r)}</span>;
const Occ = OccPct;
const filmName = (r) => r.title_he || r.title;
const filmPoster = (r, size) => <Poster posterUrl={r.poster_url} genre={r.genre} letter={(filmName(r) || '?')[0]} size={size} />;

function Weeks({ r }) {
  if (r.premiere_date) {
    const [, m, d] = r.premiere_date.split('-');
    return <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--presale)', background: 'var(--presale-bg)', padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>עולה ב-{ltr(`${Number(d)}.${Number(m)}`)}</span>;
  }
  return r.still_showing
    ? <span style={{ color: 'var(--muted)' }}>{r.weeks_in_release}</span>
    : <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', background: 'var(--surf-alt)', border: '1px solid var(--brd)', padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>ירד מהאקרנים</span>;
}

const numCol = (key, header, accent) => ({
  key, header, align: 'center',
  render: (r) => <span style={{ fontWeight: accent ? 700 : 400, color: accent ? 'var(--accent)' : 'var(--muted)' }}>{fmt(r[key])}</span>,
});
const filmCols = (cat) => [
  { key: 'title', header: 'סרט', align: 'right', sortValue: filmName, render: (r) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {filmPoster(r, 'sm')}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--txt)' }}>{filmName(r)}</div>
        {r.title_en && r.title_en !== filmName(r) && <div style={{ fontSize: 11, color: 'var(--faint)' }}>{r.title_en}</div>}
      </div>
    </div>
  ) },
  numCol('tickets', cat === 'imax' ? 'כרטיסים ב-IMAX' : 'כרטיסים', true),
  { key: 'share', header: 'נתח', align: 'center', render: (r) => <Share r={r} /> },
  { key: 'occupancy', header: 'תפוסה', align: 'center', render: (r) => <Occ v={r.occupancy} /> },
  numCol('screenings', 'הקרנות'),
  cat === 'imax'
    ? { key: 'of_film', header: 'מכלל הסרט', align: 'center', render: (r) => <span dir="ltr" style={{ unicodeBidi: 'isolate', color: 'var(--muted)' }}>{r.of_film}%</span> }
    : { key: 'weeks_in_release', header: 'שבועות', align: 'center',
        sortValue: (r) => (r.premiere_date ? -2 : r.still_showing ? r.weeks_in_release : -1), render: (r) => <Weeks r={r} /> },
];
const groupCols = (cat) => [
  { key: 'name', header: GROUP_HEAD[cat], align: 'right', sortValue: (r) => r.name,
    render: (r) => <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--txt)' }}>{groupName(cat, r.name)}</span> },
  numCol('tickets', 'כרטיסים', true),
  { key: 'share', header: 'נתח', align: 'center', render: (r) => <Share r={r} /> },
  { key: 'occupancy', header: 'תפוסה', align: 'center', render: (r) => <Occ v={r.occupancy} /> },
  numCol('films', 'סרטים'),
  { key: 'top', header: 'מוביל', align: 'right', sortValue: (r) => filmName(r.top), render: (r) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {filmPoster(r.top, 'sm')}
      <span style={{ fontSize: 12.5, color: 'var(--txt)' }}>{filmName(r.top)}</span>
    </div>
  ) },
];

/* ── the three totals ──────────────────────────────────────────────────── */
function PeriodCards({ totals, view, today, mob, onPick, onAll, onCustom }) {
  const [, m] = ymd(today);
  const [, , d] = ymd(today);
  const cards = [
    { key: 'year', label: today.slice(0, 4) },
    { key: 'month', label: HEB_MONTHS[m - 1] },
    { key: 'day', label: <>היום · {ltr(`${d}.${m}`)}</> },
  ];
  const quiet = (on) => ({ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'Heebo, sans-serif',
                            fontSize: 12.5, textDecoration: 'underline', color: on ? 'var(--accent)' : 'var(--faint)', fontWeight: on ? 700 : 400 });
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: mob ? 10 : 16 }}>
        {cards.map((p) => {
          const t = totals?.[p.key];
          const on = view.period === p.key && isCurrent(view, today);
          const lead = t?.leader;
          return (
            <div key={p.key} data-period-card={p.key} onClick={() => onPick(p.key)} style={{
              ...card, cursor: 'pointer', padding: mob ? '14px 16px' : '22px 24px',
              border: on ? '2px solid var(--accent)' : '2px solid transparent',
              display: mob ? 'flex' : 'block', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: mob ? 14 : 15, fontWeight: 800, color: 'var(--muted)' }}>{p.label}</div>
                {!mob && <div data-total style={{ fontSize: 40, fontWeight: 900, color: 'var(--accent)', letterSpacing: -1.5, lineHeight: 1.1, margin: '8px 0 12px' }}>{t ? fmt(t.tickets) : '…'}</div>}
                {lead && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: mob ? 4 : 0, minWidth: 0 }}>
                    {filmPoster(lead, 'sm')}
                    <span style={{ fontSize: 12, color: 'var(--faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      מוביל: <b style={{ color: 'var(--txt)', fontWeight: 700 }}>{filmName(lead)}</b>
                    </span>
                  </div>
                )}
              </div>
              {mob && <div data-total style={{ fontSize: 26, fontWeight: 900, color: 'var(--accent)', letterSpacing: -1, flexShrink: 0 }}>{t ? fmt(t.tickets) : '…'}</div>}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 16, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
        <button data-link="all" style={quiet(view.period === 'all')} onClick={onAll}>כל הזמנים</button>
        <button data-link="custom" style={quiet(view.period === 'custom')} onClick={onCustom}>טווח מותאם</button>
      </div>
    </div>
  );
}

/* ── categories ────────────────────────────────────────────────────────── */
function CatChips({ view, data, set }) {
  const { cat } = view;
  const row = { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ ...row, gap: 8 }}>
        {CATS.map((c) => <Chip key={c.key} label={c.label} active={cat === c.key}
                               onClick={() => set({ cat: c.key, sub: null, dub: 'all' })} />)}
      </div>
      {cat === 'dubbed' && (
        <div style={row}>
          <Chip small label="כל הכרטיסים" active={view.dub !== 'dubbed'} onClick={() => set({ dub: 'all' })} />
          <Chip small label="רק הקרנות מדובבות" active={view.dub === 'dubbed'} onClick={() => set({ dub: 'dubbed' })} />
        </div>
      )}
      {cat in GROUP_ALL && view.sub && (
        <div style={row}>
          <Chip small label={GROUP_ALL[cat]} onClick={() => set({ sub: null })} />
          {data?.sub_label && <Chip small label={groupName(cat, data.sub_label)} active />}
        </div>
      )}
    </div>
  );
}

/* ── the ranking ───────────────────────────────────────────────────────── */
function PeriodNav({ view, today, onStep }) {
  const steppable = ['year', 'month', 'day'].includes(view.period);
  const atNow = isCurrent(view, today);
  return (
    <Stepper label={periodLabel(view, today)} onPrev={steppable ? () => onStep(-1) : null}
             onNext={steppable ? () => onStep(1) : null} nextDisabled={atNow} />
  );
}

function Unassigned({ cat, info, admin, onMovieClick }) {
  const [open, setOpen] = useState(false);
  if (!info || !info.films) return null;
  return (
    <div style={{ marginTop: 12, borderRadius: 10, background: 'var(--surf-alt)', fontSize: 13, color: 'var(--muted)' }}>
      <div onClick={admin ? () => setOpen((o) => !o) : undefined} data-unassigned style={{
        display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between', flexWrap: 'wrap',
        padding: '10px 14px', cursor: admin ? 'pointer' : 'default',
      }}>
        <span>{NONE_LABEL[cat]} · {fmt(info.films)} סרטים · {fmt(info.tickets)} כרטיסים</span>
        {admin && <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{open ? 'סגירה' : 'להשלמה ←'}</span>}
      </div>
      {open && (
        <div style={{ padding: '0 14px 8px' }}>
          {info.rows.map((r) => (
            <div key={r.movie_id} onClick={() => onMovieClick && onMovieClick(r.movie_id)} style={{
              display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderTop: '1px solid var(--brd)', cursor: 'pointer',
            }}>
              <span style={{ color: 'var(--txt)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{filmName(r)}</span>
              <span style={{ flexShrink: 0 }}>{fmt(r.tickets)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RankingBody({ view, data, loading, today, mob, admin, set, onStep, onMovieClick }) {
  const [shown, setShown] = useState(20);
  useEffect(() => { setShown(20); }, [view.cat, view.sub, view.dub, view.period, view.anchor]);
  if (!data) return <div style={{ ...card, textAlign: 'center', color: 'var(--faint)', padding: 40 }}>{loading ? 'טוען דירוגים...' : 'אין נתונים'}</div>;

  const groups = data.kind === 'groups';
  const cat = view.cat;
  const rows = data.rows || [];
  let title = CAT_TITLE[cat];
  if (cat in GROUP_ALL && view.sub) title = groupName(cat, data.sub_label || '');
  const line = groups
    ? `${fmt(data.count)} ${GROUP_WORD[cat]}`
    : `${fmt(data.screenings)} הקרנות · ${fmt(data.count)} סרטים`;
  const openFilm = (r) => onMovieClick && onMovieClick(r.movie_id);
  const openGroup = (r) => set({ sub: r.key });

  return (
    <div data-ranking style={{ ...card, padding: mob ? 16 : 22, opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
      <Head title={title} mob={mob} total={data.total} line={line}
            nav={<PeriodNav view={view} today={today} onStep={onStep} />} />
      {rows.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--faint)', padding: 30, fontSize: 13 }}>אין נתונים לתקופה זו</div>
      ) : (
        <>
          <Podium items={rows} mob={mob} max={5}
            itemKey={(r) => (groups ? r.key : r.movie_id)}
            onClick={groups ? openGroup : openFilm}
            poster={(r) => filmPoster(groups ? r.top : r, 'xl')}
            name={(r) => (groups ? groupName(cat, r.name) : filmName(r))}
            value={(r) => r.tickets}
            extra={groups ? (r) => <span style={{ fontSize: 11, color: 'var(--faint)' }}>{fmt(r.films)} סרטים</span> : undefined} />
          {mob ? (
            <div data-phone-list>
              {rows.slice(0, shown).map((r, i) => (groups ? (
                <PhoneRow key={r.key} i={i} poster={filmPoster(r.top, 'sm')} name={groupName(cat, r.name)}
                          sub={`${fmt(r.films)} סרטים · מוביל: ${filmName(r.top)}`} tickets={r.tickets}
                          line1={<>נתח {ltr(sharePct(r))} · <Occ v={r.occupancy} /></>} onClick={() => openGroup(r)} />
              ) : (
                <PhoneRow key={r.movie_id} i={i} poster={filmPoster(r, 'sm')} name={filmName(r)}
                          sub={r.title_en && r.title_en !== filmName(r) ? r.title_en : null} tickets={r.tickets}
                          line1={<>נתח {ltr(sharePct(r))} · <Occ v={r.occupancy} /></>}
                          line2={cat === 'imax' ? `${fmt(r.screenings)} הקרנות · ${r.of_film}% מהסרט` : `${fmt(r.screenings)} הקרנות`}
                          onClick={() => openFilm(r)} />
              )))}
              {rows.length > shown && <MoreButton onClick={() => setShown((n) => n + 20)}>הצג עוד</MoreButton>}
            </div>
          ) : (
            <RankingTable columns={groups ? groupCols(cat) : filmCols(cat)} data={rows} rankColumn pageSize={20}
                          initialSort={{ key: 'tickets', dir: 'desc' }} minWidth={620}
                          onRowClick={groups ? openGroup : openFilm} />
          )}
        </>
      )}
      {groups && cat in NONE_LABEL && (
        <Unassigned cat={cat} info={data.unassigned} admin={admin} onMovieClick={onMovieClick} />
      )}
    </div>
  );
}

/* ── the page ──────────────────────────────────────────────────────────── */
function RankingsPage({ onMovieClick, refreshNonce, view: viewProp, onViewChange }) {
  const [own, setOwn] = useState(defaultRankView);
  const view = viewProp || own;
  const setView = onViewChange || setOwn;
  const set = (patch) => setView({ ...view, ...patch });
  const today = israelToday();
  const mob = useWindowWidth() < 768;
  const { user } = useAuth();
  const admin = !!user?.is_admin;

  const [totals, setTotals] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);

  useEffect(() => {
    fetchRankingTotals().then(setTotals).catch(() => setTotals(null));
  }, [refreshNonce]);

  const range = rangeOf(view);
  useEffect(() => {
    const my = ++seq.current;
    setLoading(true);
    fetchRankings({ category: view.cat, sub: view.sub, dub_count: view.cat === 'dubbed' ? view.dub : undefined,
                    start: range?.start, end: range?.end })
      .then((d) => { if (my === seq.current) { setData(d); setLoading(false); } })
      .catch(() => { if (my === seq.current) { setData(null); setLoading(false); } });
  }, [view.cat, view.sub, view.dub, view.period, view.anchor, range?.start, range?.end, refreshNonce]); // eslint-disable-line

  const pick = (period) => setView({ ...view, period, anchor: today, custom: null });
  const custom = () => setView({ ...view, period: 'custom',
                                 custom: view.custom || { start: `${today.slice(0, 8)}01`, end: today } });

  return (
    <div style={{ maxWidth: PAGE_MAX, margin: '0 auto', width: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PeriodCards totals={totals} view={view} today={today} mob={mob}
                   onPick={pick} onAll={() => setView({ ...view, period: 'all', custom: null })} onCustom={custom} />
      {view.period === 'custom' && view.custom && (
        <CustomRange value={view.custom} onChange={(c) => setView({ ...view, custom: c })} />
      )}
      <CatChips view={view} data={data} set={set} />
      <RankingBody view={view} data={data} loading={loading} today={today} mob={mob} admin={admin}
                   set={set} onStep={(dir) => set({ anchor: stepAnchor(view, dir) })} onMovieClick={onMovieClick} />
    </div>
  );
}

export default RankingsPage;
